import React, { useCallback, useEffect, useState } from 'react';
import { createPortal } from 'react-dom';
import { fmtMeasure } from './measureFormat';

/**
 * Measurements for one COA activity: the quantitative result, broken out by the
 * case type CMS actually tests.
 *
 * Everything here is an aggregate. The numbers are computed by the source (for
 * universe timeliness, the same evaluators CMS applies at the universe level in
 * a program audit) and are read-only: a user may set a threshold, never a
 * result. No case-level data exists in this payload to display.
 *
 * Target is deliberately blank until someone sets it. CMS publishes no passing
 * percentage for universe timeliness — an untimely case is a deficiency — so any
 * threshold is the organization's own business choice and must be entered, not
 * invented by us.
 */

const API = process.env.REACT_APP_API_URL || '';

// A measured value in the unit its benchmark was set in ("2:00" for 120
// seconds, "95%" for a rate); see measureFormat.js.
const pct = (v, unit) => fmtMeasure(v, unit || 'percent');

function RateBadge({ value, thresholdMet, unit }) {
  if (value === null || value === undefined) {
    return <span className="text-slate-400 text-xs">no cases in scope</span>;
  }
  const tone = thresholdMet === false
    ? 'bg-rose-50 text-rose-700 border-rose-200'
    : thresholdMet === true
      ? 'bg-emerald-50 text-emerald-700 border-emerald-200'
      : 'bg-slate-50 text-slate-700 border-slate-200';
  return (
    <span className={`inline-flex items-center gap-1 rounded-lg border px-2 py-0.5 text-sm font-semibold ${tone}`}>
      {pct(value, unit)}
      {thresholdMet === false && <span aria-hidden>&#9888;</span>}
      {thresholdMet === true && <span aria-hidden>&#10003;</span>}
    </span>
  );
}

export default function MeasurementsModal({ activityId, activityName, canEditThreshold, onClose }) {
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [metrics, setMetrics] = useState([]);
  const [savingId, setSavingId] = useState(null);
  const [draftTarget, setDraftTarget] = useState({});

  const load = useCallback(() => {
    setLoading(true);
    setError('');
    fetch(`${API}/api/coa/activities/${activityId}`, { credentials: 'include' })
      .then((r) => r.json())
      .then((d) => {
        if (!d.success) throw new Error(d.message || 'Could not load measurements.');
        setMetrics(d.activity?.metrics || []);
      })
      .catch((e) => setError(e.message))
      .finally(() => setLoading(false));
  }, [activityId]);

  useEffect(() => { load(); }, [load]);

  const saveTarget = (metric) => {
    const raw = draftTarget[metric.id];
    const target = raw === '' || raw === undefined ? null : Number(raw);
    if (target !== null && (!Number.isFinite(target) || target < 0 || target > 100)) {
      setError('Target must be a number between 0 and 100, or blank to clear it.');
      return;
    }
    setSavingId(metric.id);
    setError('');
    fetch(`${API}/api/coa/activities/${activityId}/metrics/${metric.id}`, {
      method: 'PATCH',
      credentials: 'include',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify({ target }),
    })
      .then((r) => r.json())
      .then((d) => {
        if (!d.success) throw new Error(d.message || 'Could not save the threshold.');
        setMetrics((prev) => prev.map((m) => (m.id === d.metric.id ? { ...m, ...d.metric } : m)));
        setDraftTarget((prev) => { const next = { ...prev }; delete next[metric.id]; return next; });
      })
      .catch((e) => setError(e.message))
      .finally(() => setSavingId(null));
  };

  const groups = [...new Set(metrics.map((m) => m.metric_group || '—'))];
  const totals = metrics.reduce((acc, m) => ({
    timely: acc.timely + (m.numerator || 0),
    applicable: acc.applicable + (m.denominator || 0),
    deficiencies: acc.deficiencies + (m.deficiency_count || 0),
    excluded: acc.excluded + (m.excluded_count || 0),
  }), { timely: 0, applicable: 0, deficiencies: 0, excluded: 0 });
  const overall = totals.applicable ? Math.round((totals.timely / totals.applicable) * 1000) / 10 : null;

  // Portal to <body> — see COARecordModal for why.
  return createPortal(
    <div className="fixed inset-0 z-50 flex items-center justify-center bg-slate-900/40 p-4" onClick={onClose}>
      <div
        className="w-[min(72rem,95vw)] max-w-6xl max-h-[85vh] overflow-y-auto rounded-2xl bg-white shadow-xl"
        onClick={(e) => e.stopPropagation()}
      >
        <div className="sticky top-0 flex items-start justify-between gap-4 border-b border-slate-200 bg-white px-6 py-4 rounded-t-2xl">
          <div>
            <h2 className="text-lg font-semibold text-slate-900">Measurements</h2>
            <p className="text-sm text-slate-500">{activityName}</p>
          </div>
          <button
            onClick={onClose}
            className="rounded-lg px-3 py-1.5 text-sm text-slate-500 hover:bg-slate-100"
          >
            Close
          </button>
        </div>

        <div className="px-6 py-5">
          {loading && <p className="text-sm text-slate-500">Loading measurements…</p>}
          {error && (
            <div className="mb-4 rounded-lg border border-rose-200 bg-rose-50 px-4 py-2 text-sm text-rose-700">{error}</div>
          )}

          {!loading && metrics.length === 0 && (
            <p className="text-sm text-slate-500">This activity carries no measurements.</p>
          )}

          {!loading && metrics.length > 0 && (
            <>
              <div className="mb-5 flex flex-wrap items-center gap-x-6 gap-y-2 rounded-xl bg-slate-50 px-4 py-3">
                <div>
                  <div className="text-xs text-slate-500">Overall</div>
                  <div className="text-xl font-semibold text-slate-900">{pct(overall)}</div>
                </div>
                <div>
                  <div className="text-xs text-slate-500">Timely</div>
                  <div className="text-sm text-slate-700">{totals.timely} of {totals.applicable} cases</div>
                </div>
                <div>
                  <div className="text-xs text-slate-500">Untimely</div>
                  <div className={`text-sm font-medium ${totals.deficiencies ? 'text-rose-700' : 'text-slate-700'}`}>
                    {totals.deficiencies}
                  </div>
                </div>
                {totals.excluded > 0 && (
                  <div>
                    <div className="text-xs text-slate-500">Excluded</div>
                    <div className="text-sm text-slate-700">{totals.excluded}</div>
                  </div>
                )}
              </div>

              {groups.map((group) => (
                <div key={group} className="mb-6">
                  <h3 className="mb-2 text-sm font-semibold text-slate-800">{group}</h3>
                  <div className="overflow-x-auto">
                    <table className="w-full text-sm">
                      <thead>
                        <tr className="border-b border-slate-200 text-left text-xs uppercase tracking-wide text-slate-500">
                          <th className="py-2 pr-3 font-medium">Case type</th>
                          <th className="py-2 pr-3 font-medium">Result</th>
                          <th className="py-2 pr-3 font-medium">Basis</th>
                          <th className="py-2 pr-3 font-medium">Untimely</th>
                          <th className="py-2 pr-3 font-medium">Target</th>
                        </tr>
                      </thead>
                      <tbody>
                        {metrics.filter((m) => (m.metric_group || '—') === group).map((m) => {
                          const editing = draftTarget[m.id] !== undefined;
                          return (
                            <tr key={m.id} className="border-b border-slate-100">
                              <td className="py-2.5 pr-3 text-slate-800">{m.metric_label}</td>
                              <td className="py-2.5 pr-3">
                                <RateBadge value={m.value === null ? null : Number(m.value)} thresholdMet={m.threshold_met} unit={m.unit} />
                              </td>
                              <td className="py-2.5 pr-3 text-slate-500">
                                {m.metric_group === 'Data errors'
                                  ? `${Math.round(Number(m.value) || 0)} ${m.metric_key === 'errors.total' ? 'errors in the file, besides timeliness' : 'cells flagged'}`
                                  : m.denominator
                                    ? `${m.numerator} of ${m.denominator} timely`
                                    : 'no cases in scope'}
                                {m.excluded_count > 0 && `, ${m.excluded_count} excluded`}
                              </td>
                              <td className={`py-2.5 pr-3 ${m.deficiency_count ? 'font-medium text-rose-700' : 'text-slate-500'}`}>
                                {m.metric_group === 'Data errors' ? '' : (m.deficiency_count || 0)}
                              </td>
                              <td className="py-2.5 pr-3">
                                {!canEditThreshold ? (
                                  <span className="text-slate-500">{m.target === null ? 'not set' : pct(m.target)}</span>
                                ) : (
                                  <div className="flex items-center gap-1.5">
                                    <input
                                      type="number"
                                      min="0"
                                      max="100"
                                      step="0.1"
                                      placeholder="not set"
                                      value={editing ? draftTarget[m.id] : (m.target === null ? '' : m.target)}
                                      onChange={(e) => setDraftTarget((p) => ({ ...p, [m.id]: e.target.value }))}
                                      className="w-20 rounded-lg border border-slate-200 px-2 py-1 text-sm focus:border-orange-400 focus:outline-none focus:ring-2 focus:ring-orange-500/20"
                                    />
                                    {editing && (
                                      <button
                                        onClick={() => saveTarget(m)}
                                        disabled={savingId === m.id}
                                        className="rounded-lg bg-slate-800 px-2 py-1 text-xs text-white hover:bg-slate-700 disabled:opacity-50"
                                      >
                                        {savingId === m.id ? '…' : 'Save'}
                                      </button>
                                    )}
                                  </div>
                                )}
                              </td>
                            </tr>
                          );
                        })}
                      </tbody>
                    </table>
                  </div>
                </div>
              ))}

              <p className="mt-2 text-xs leading-relaxed text-slate-500">
                Each case type is measured against its own CMS timeliness standard, the same test applied at the
                universe level in a program audit. Results are counts and rates computed at source and cannot be
                edited here. No case-level data is retained.
                {canEditThreshold && ' A target is your organization\'s own threshold; CMS does not publish one.'}
              </p>
            </>
          )}
        </div>
      </div>
    </div>,
    document.body
  );
}
